import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { Eye, EyeOff, KeyRound } from "lucide-react";
import { toast } from "react-toastify";
import {
  useLazyCheckShopMemberUsernameAvailabilityQuery,
  useLazyCheckShopMemberPasswordAvailabilityQuery,
  useSetShopMemberCredentialsMutation,
} from "../../../../features/shop/shopMemberApiSlice";
import { decodeId } from "../../../../utils/idObfuscator";
import "../ModalEmail/style.css";

const USERNAME_PATTERN = /^[a-z][a-z0-9._-]{2,19}$/i;
const PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9\s])\S{12,128}$/;

const AccessModal = ({
  handleCloseaccess,
  showaccess,
  email,
  onGrantAccess,
  shopId,
  membershipId,
}) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [checkUsername, usernameAvailability] =
    useLazyCheckShopMemberUsernameAvailabilityQuery();
  const [checkPassword, passwordAvailability] =
    useLazyCheckShopMemberPasswordAvailabilityQuery();
  const [setCredentials, { isLoading: isSaving }] =
    useSetShopMemberCredentialsMutation();

  const cleanShopId = decodeId(shopId) || shopId || "";
  const cleanMembershipId = decodeId(membershipId) || membershipId || "";

  const normalizedUsername = username.trim().toLowerCase();
  const usernameFormatIsValid = USERNAME_PATTERN.test(normalizedUsername);
  const matchingAvailability =
    usernameAvailability.originalArgs?.username === normalizedUsername &&
    usernameAvailability.originalArgs?.membershipId === cleanMembershipId;
  const usernameAvailable = matchingAvailability
    ? usernameAvailability.currentData?.data?.available
    : undefined;
  const usernameError = !normalizedUsername
    ? "Username is required."
    : !usernameFormatIsValid
      ? "Use 3–20 characters, start with a letter, and use letters, numbers, dots, underscores, or hyphens."
      : usernameAvailable === false
        ? "This username is already taken."
        : usernameAvailability.isError && matchingAvailability
          ? usernameAvailability.error?.data?.message ||
            "Unable to check username availability."
          : "";

  const passwordFormatIsValid = PASSWORD_PATTERN.test(password);
  const matchingPasswordAvailability =
    passwordAvailability.originalArgs?.password === password &&
    passwordAvailability.originalArgs?.membershipId === cleanMembershipId;
  const passwordAvailable = matchingPasswordAvailability
    ? passwordAvailability.currentData?.data?.available
    : undefined;

  const passwordError = !password
    ? "Password is required."
    : !passwordFormatIsValid
      ? "Use 12–128 characters with uppercase and lowercase letters, a number, and a symbol."
      : passwordAvailable === false
        ? "This password is already in use by another shop member. Please create a unique password."
        : passwordAvailability.isError && matchingPasswordAvailability
          ? passwordAvailability.error?.data?.message ||
            "Unable to verify password uniqueness."
          : "";

  const formIsValid =
    usernameFormatIsValid &&
    usernameAvailable === true &&
    passwordFormatIsValid &&
    passwordAvailable === true &&
    Boolean(cleanMembershipId);

  useEffect(() => {
    if (
      !showaccess ||
      !cleanMembershipId ||
      !usernameFormatIsValid
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      checkUsername({
        shopId: cleanShopId,
        username: normalizedUsername,
        membershipId: cleanMembershipId,
      });
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [
    checkUsername,
    cleanMembershipId,
    cleanShopId,
    normalizedUsername,
    showaccess,
    usernameFormatIsValid,
  ]);

  useEffect(() => {
    if (
      !showaccess ||
      !cleanMembershipId ||
      !passwordFormatIsValid
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      checkPassword({
        shopId: cleanShopId,
        password,
        membershipId: cleanMembershipId,
      });
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [
    checkPassword,
    cleanMembershipId,
    cleanShopId,
    password,
    passwordFormatIsValid,
    showaccess,
  ]);

  const clearForm = () => {
    setUsername("");
    setPassword("");
    setShowPassword(false);
    setSubmitted(false);
    setSubmitError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitted(true);
    setSubmitError("");

    if (!formIsValid) {
      if (!usernameFormatIsValid) {
        setSubmitError(usernameError || "Please enter a valid username.");
        return;
      }
      if (usernameAvailable !== true) {
        setSubmitError(
          usernameAvailable === false
            ? "This username is already taken."
            : usernameError || "Checking username availability...",
        );
        return;
      }
      if (!passwordFormatIsValid) {
        setSubmitError(passwordError || "Please enter a strong password.");
        return;
      }
      if (passwordAvailable !== true) {
        setSubmitError(
          passwordAvailable === false
            ? "This password is already in use by another shop member."
            : passwordError || "Checking password uniqueness...",
        );
        return;
      }
      if (!cleanMembershipId) {
        setSubmitError("Membership record missing. Please try inviting again.");
        return;
      }
      return;
    }

    try {
      const result = await setCredentials({
        shopId: cleanShopId,
        membershipId: cleanMembershipId,
        username: normalizedUsername,
        password,
      }).unwrap();
      const sharePath = result?.data?.membership?.sharePath;
      if (sharePath) {
        const fullShareUrl = `${window.location.origin}${sharePath}`;
        navigator.clipboard?.writeText?.(fullShareUrl).catch(() => {});
        toast.success(
          "Shop access invitation created! Protected share link copied to clipboard.",
          { position: "top-center" },
        );
      } else {
        toast.success("Shop access granted!", { position: "top-center" });
      }
      if (!result.notificationSent) {
        toast.warn(
          "The invitation was saved, but the member notification could not be sent.",
          { position: "top-center" },
        );
      }
      onGrantAccess(normalizedUsername, result?.data?.membership);
      clearForm();
      handleCloseaccess();
    } catch (error) {
      setSubmitError(
        error?.data?.message || "Unable to save shop access. Please try again.",
      );
    }
  };

  const handleDismiss = () => {
    clearForm();
    handleCloseaccess();
  };

  return (
    <Modal
      className="shop-invite-modal shop-access-setup-modal"
      centered
      show={showaccess}
      onHide={handleDismiss}
    >
      <Modal.Header className="shop-invite-header" closeButton>
        <div className="shop-invite-heading">
          <span className="shop-invite-heading-icon">
            <KeyRound size={20} />
          </span>
          <div>
            <span className="shop-invite-step-badge">Step 2 of 2</span>
            <Modal.Title>Set up shop access</Modal.Title>
            <p>Create the login details for this member.</p>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="shop-invite-body">
        {email && <p className="shop-member-access-email">{email}</p>}
        <Form noValidate onSubmit={handleSubmit}>
          <Form.Group className="shop-access-setup-field">
            <Form.Label htmlFor="shop-access-username">Username</Form.Label>
            <Form.Control
              id="shop-access-username"
              type="text"
              value={username}
              autoComplete="username"
              placeholder="Create a username"
              required
              maxLength={20}
              isInvalid={submitted && Boolean(usernameError)}
              aria-describedby="shop-access-username-hint shop-access-username-error"
              onChange={(event) => {
                setUsername(event.target.value);
                setSubmitError("");
              }}
            />
            <Form.Text id="shop-access-username-hint">
              3–20 characters, starting with a letter. Usernames must be unique.
            </Form.Text>
            {usernameFormatIsValid && showaccess && (
              <div className="shop-invite-hint" role="status">
                {usernameAvailability.isFetching && matchingAvailability
                  ? "Checking username..."
                  : usernameAvailable === true
                    ? "Username is available."
                    : usernameAvailable === false
                      ? "Username is already taken."
                      : ""}
              </div>
            )}
            {submitted && usernameError && (
              <Form.Control.Feedback
                id="shop-access-username-error"
                type="invalid"
              >
                {usernameError}
              </Form.Control.Feedback>
            )}
            {submitted && !usernameError && usernameAvailable !== true && (
              <Form.Control.Feedback
                id="shop-access-username-error"
                type="invalid"
              >
                Check username availability before continuing.
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="shop-access-setup-field">
            <Form.Label htmlFor="shop-access-password">Password</Form.Label>
            <div className="shop-access-password">
              <Form.Control
                id="shop-access-password"
                type={showPassword ? "text" : "password"}
                value={password}
                autoComplete="new-password"
                placeholder="Create a strong password"
                required
                minLength={12}
                maxLength={128}
                isInvalid={submitted && Boolean(passwordError)}
                aria-describedby="shop-access-password-hint shop-access-password-error"
                onChange={(event) => setPassword(event.target.value)}
              />
              <button
                type="button"
                className="shop-access-password-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                onClick={() => setShowPassword((visible) => !visible)}
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
            <Form.Text id="shop-access-password-hint">
              Use 12–128 characters with uppercase and lowercase letters, a
              number, and a symbol. Password must be unique and protected.
            </Form.Text>
            {passwordFormatIsValid && showaccess && (
              <div className="shop-invite-hint" role="status">
                {passwordAvailability.isFetching && matchingPasswordAvailability
                  ? "Checking password uniqueness..."
                  : passwordAvailable === true
                    ? "Password is strong and unique."
                    : passwordAvailable === false
                      ? "Password is already in use. Please create a unique password."
                      : ""}
              </div>
            )}
            {submitted && passwordError && (
              <Form.Control.Feedback
                id="shop-access-password-error"
                type="invalid"
              >
                {passwordError}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {submitError && (
            <div className="shop-invite-invalid" role="alert">
              {submitError}
            </div>
          )}

          <div className="shop-invite-actions">
            <button
              type="submit"
              className="shop-invite-next"
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Give Access"}
            </button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AccessModal;
